import { FreshnessNote } from "@/components/FreshnessNote";
import { freshnessDate } from "@/lib/currentYear";
import { useMemo } from "react";
import { Link } from "@/lib/router-compat";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOHelmet } from "@/components/SEOHelmet";
import { PopularCardsSection } from "@/components/PopularCardsSection";
import { SegmentLinks } from "@/components/SegmentLinks";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@/lib/router-compat";
import { getSiteOrigin } from "@/lib/ssr-safe";

export interface TeilzahlungSearch {
  betrag: string;
  rate: string;
  zins: string;
}

export const defaultTeilzahlungSearch: TeilzahlungSearch = {
  betrag: "2000",
  rate: "100",
  zins: "",
};

const parseAmount = (value: string): number => {
  const normalized = value.replace(",", ".").replace(/[^0-9.]/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const eur = (value: number) =>
  value.toLocaleString("de-DE", { style: "currency", currency: "EUR" });

interface PayoffResult {
  months: number;
  totalInterest: number;
  totalPaid: number;
  neverPaysOff: boolean;
}

/**
 * Fixed monthly payment on a revolving balance. Monthly rate = APR / 12.
 * Closed-form amortisation; no intermediate rounding.
 */
function computePayoff(balance: number, monthlyPayment: number, aprPercent: number): PayoffResult {
  const i = aprPercent / 100 / 12;
  if (balance <= 0 || monthlyPayment <= 0) {
    return { months: 0, totalInterest: 0, totalPaid: 0, neverPaysOff: false };
  }
  if (i === 0) {
    const months = Math.ceil(balance / monthlyPayment);
    return { months, totalInterest: 0, totalPaid: balance, neverPaysOff: false };
  }
  if (monthlyPayment <= balance * i) {
    return { months: 0, totalInterest: 0, totalPaid: 0, neverPaysOff: true };
  }
  const monthsExact = -Math.log(1 - (balance * i) / monthlyPayment) / Math.log(1 + i);
  const months = Math.ceil(monthsExact);
  // Simulate the final (smaller) payment to keep totals exact.
  let remaining = balance;
  let interest = 0;
  for (let m = 0; m < months; m++) {
    const interestThisMonth = remaining * i;
    interest += interestThisMonth;
    remaining = remaining + interestThisMonth - monthlyPayment;
  }
  return {
    months,
    totalInterest: interest,
    totalPaid: balance + interest,
    neverPaysOff: false,
  };
}

interface TeilzahlungsrechnerProps {
  values: TeilzahlungSearch;
  onChange: (key: keyof TeilzahlungSearch, value: string) => void;
}

export default function Teilzahlungsrechner({ values, onChange }: TeilzahlungsrechnerProps) {
  const navigate = useNavigate();
  const domain = getSiteOrigin();
  const pageUrl = `${domain}/tools/teilzahlungsrechner`;

  const balance = parseAmount(values.betrag);
  const monthlyPayment = parseAmount(values.rate);
  const apr = parseAmount(values.zins || "22");

  const result = useMemo(
    () => computePayoff(balance, monthlyPayment, apr),
    [balance, monthlyPayment, apr],
  );

  const handleNavigate = (page: string) => {
    if (page === "home") navigate("/");
  };

  const handleViewDetails = (slug: string) => navigate(`/kreditkarten/${slug}`);

  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Startseite", item: domain },
        { "@type": "ListItem", position: 2, name: "Teilzahlungsrechner", item: pageUrl },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Teilzahlungsrechner",
      applicationCategory: "FinanceApplication",
      operatingSystem: "Web",
      url: pageUrl,
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
    },
  ];

  const inputClass =
    "w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50";

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      <SEOHelmet
        title="Teilzahlungsrechner – was kostet die Rückzahlung in Raten?"
        description="Berechnen Sie, wie lange die Rückzahlung eines Kreditkarten-Saldos dauert und wie viel Zinsen dabei anfallen — im Vergleich zur Vollzahlung."
        url={pageUrl}
        type="website"
        modifiedTime={freshnessDate()}
        additionalSchemas={schemas}
      />

      <Header onNavigate={handleNavigate} currentPage="tool-teilzahlungsrechner" />

      <main className="flex-1">
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Teilzahlungsrechner</h1>
          <FreshnessNote className="mb-3" label="Konditionen geprüft" />
          <p className="text-gray-600 mb-6">
            Viele Kreditkarten sind ab Werk auf Teilzahlung eingestellt: Sie zahlen monatlich nur
            einen Teil des offenen Betrags zurück, der Rest wird verzinst. Hier sehen Sie, was das
            konkret kostet — und wie viel Sie durch Vollzahlung sparen.
          </p>

          <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <div className="grid gap-4 sm:grid-cols-3">
              <label className="block">
                <span className="text-sm font-medium text-gray-900">Offener Betrag</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={values.betrag}
                  onChange={(e) => onChange("betrag", e.target.value)}
                  className={inputClass}
                  aria-describedby="hint-betrag"
                />
                <span id="hint-betrag" className="text-xs text-muted-foreground">
                  Aktueller Saldo in Euro
                </span>
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-900">Monatliche Rückzahlung</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={values.rate}
                  onChange={(e) => onChange("rate", e.target.value)}
                  className={inputClass}
                  aria-describedby="hint-rate"
                />
                <span id="hint-rate" className="text-xs text-muted-foreground">
                  Fester Betrag pro Monat
                </span>
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-900">Effektiver Jahreszins</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={values.zins}
                  placeholder="22"
                  onChange={(e) => onChange("zins", e.target.value)}
                  className={inputClass}
                  aria-describedby="hint-zins"
                />
                <span id="hint-zins" className="text-xs text-muted-foreground">
                  In Prozent, laut Preisverzeichnis der Karte
                </span>
              </label>
            </div>

            <div className="mt-6 border-t border-border pt-6" aria-live="polite">
              {result.neverPaysOff ? (
                <p className="text-sm font-medium text-red-700">
                  Mit dieser monatlichen Rate wird der Betrag nie abbezahlt — die Rate ist kleiner
                  als die monatlich anfallenden Zinsen. Erhöhen Sie die Rückzahlung oder stellen
                  Sie auf Vollzahlung um.
                </p>
              ) : result.months > 0 ? (
                <dl className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                      Laufzeit bis zur vollständigen Rückzahlung
                    </dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      {result.months} {result.months === 1 ? "Monat" : "Monate"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                      Zinskosten gesamt
                    </dt>
                    <dd className="text-2xl font-bold text-gray-900">{eur(result.totalInterest)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                      Ersparnis bei Vollzahlung
                    </dt>
                    <dd className="text-2xl font-bold text-gray-900">{eur(result.totalInterest)}</dd>
                  </div>
                </dl>
              ) : (
                <p className="text-sm text-gray-600">
                  Geben Sie Betrag und monatliche Rückzahlung ein, um die Berechnung zu starten.
                </p>
              )}
            </div>
          </div>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              Vollzahlung oder Teilzahlung?
            </h2>
            <p className="text-gray-600 mb-3">
              Bei Vollzahlung wird der komplette Rechnungsbetrag einmal im Monat per Lastschrift
              eingezogen — es fallen keine Sollzinsen an. Bei Teilzahlung zahlen Sie nur einen Teil
              zurück und verzinsen den Rest. Effektive Jahreszinsen von 20 % und mehr sind bei
              Kreditkarten keine Seltenheit.
            </p>
            <p className="text-gray-600 mb-3">
              Die Einstellung lässt sich bei fast allen Anbietern im Kundenportal ändern. Wer die
              Karte diszipliniert nutzt, fährt mit Vollzahlung immer am günstigsten.
            </p>
            <p className="text-gray-600">
              Noch ohne passende Karte? Der{" "}
              <Link to="/tools/kreditkarten-finder" className="text-primary underline">
                Kreditkarten-Finder
              </Link>{" "}
              zeigt in fünf Fragen die passenden Karten, der{" "}
              <Link to="/tools/kostenrechner" className="text-primary underline">
                Kostenrechner
              </Link>{" "}
              vergleicht die jährlichen Kosten.
            </p>
          </section>

          <p className="text-xs text-muted-foreground mt-8">
            Die Berechnung dient der Orientierung und berücksichtigt keine neuen Umsätze während
            der Rückzahlung. Maßgeblich ist das Preis- und Leistungsverzeichnis Ihres Anbieters.
            Keine Finanzberatung.
          </p>

          <div className="mt-6">
            <Button variant="outline" onClick={() => navigate("/kreditkarten")}>
              Alle Kreditkarten im Vergleich
            </Button>
          </div>
        </section>

        <SegmentLinks />

        {/* Gängige Kreditkarten – always shown directly above the footer */}
        <PopularCardsSection
          currentPage="tool-teilzahlungsrechner"
          forceVisible
          onViewDetails={handleViewDetails}
          onViewAllCards={() => navigate("/kreditkarten")}
        />
      </main>

      <Footer onNavigate={handleNavigate} />
    </div>
  );
}
